import React from 'react';
import { Alert, Grid, styled } from '@mui/material';
import { PrimaryButton, ModalHeader, Text, Colors } from 'library';
import { SignUpProps } from 'models';
import ConsentCheckbox from './Consent';
import ReferralCodeInput from './ReferralCodeInput';
import PhoneNumberVerification from './PhoneNumberVerification';
import PersonalInfoSection from './sections/PersonalInfoSection';
import AddressSection from './sections/AddressSection';
import AccountSection from './sections/AccountSection';
import BusinessDetailsSection from './sections/BusinessDetailsSection';
import useSignUpFormState from './useSignUpFormState';

export default function SignupForm({
    location,
    onComplete,
    onClose,
    existingEmail,
    existingPhoneNumber,
    message = 'Create Account',
    redirect = true,
    businessSignup: isBusinessSignup = false,
    actionMessage = 'Sign Up',
    subtitle,
    skipToPhone = false,
    businessType = 'CHILD_CARE',
    hideHeader = false,
    onNotBusinessClick,
}: SignUpProps) {
    const form = useSignUpFormState({
        existingEmail,
        existingPhoneNumber,
        redirect,
        isBusinessSignup,
        businessType,
        location,
        onClose,
        onComplete,
    });

    if (skipToPhone) {
        return (
            <Grid container item direction="column" spacing={1}>
                {!hideHeader && <ModalHeader onClose={onClose} title="Verify Phone Number" subtitle={subtitle} />}
                <PhoneNumberVerification
                    phoneNumber={form.user?.phone_number ?? form.phoneNumber}
                    handleSuccess={form.finallyComplete}
                />
                <FormFooter />
            </Grid>
        );
    }

    return (
        <Grid container item direction="column">
            {!hideHeader && <ModalHeader onClose={onClose} title={message} subtitle={subtitle} />}

            <PersonalInfoSection
                firstName={form.firstName}
                setFirstName={form.setFirstName}
                lastName={form.lastName}
                setLastName={form.setLastName}
                phoneDisplay={form.display}
                onPhoneChange={(value, displayPhone) => {
                    form.setPhoneNumber(value);
                    form.setDisplay(displayPhone);
                }}
                phoneError={form.phoneError}
                phoneErrorText={form.phoneErrorText}
                expanded={form.activeSection === 'personal'}
                complete={form.personalComplete}
                onToggle={() => form.toggleSection('personal')}
                onNext={() => form.goToNextFrom('personal')}
            />

            <AddressSection
                setValid={form.setIsAddressValid}
                setAddressPatchBody={form.setAddress}
                expanded={form.activeSection === 'address'}
                complete={form.addressComplete}
                onToggle={() => form.toggleSection('address')}
                onNext={() => form.goToNextFrom('address')}
            />

            <AccountSection
                email={form.email}
                isEmail={form.isEmail}
                onEmailChange={(value, isValid) => {
                    form.setIsEmail(isValid);
                    form.setEmail(value);
                    form.setValid(isValid);
                }}
                password={form.password}
                isPassword={form.isPassword}
                passwordErrorText={form.passwordErrorText}
                onPasswordChange={(value, isValid) => {
                    form.setPassword(value);
                    form.setIsPassword(isValid);
                }}
                expanded={form.activeSection === 'account'}
                complete={form.accountComplete}
                onToggle={() => form.toggleSection('account')}
                onNext={() => form.goToNextFrom('account')}
            />

            {form.showBusiness && (
                <BusinessDetailsSection
                    firstName={form.firstName}
                    state={form.address?.state}
                    isValidProgramId={form.isValidProgramId}
                    onBusinessDataChange={form.setBusinessData}
                    onBusinessErrorChange={form.setBusinessError}
                    onNotBusinessClick={onNotBusinessClick}
                    expanded={form.activeSection === 'business'}
                    complete={form.businessComplete}
                    onToggle={() => form.toggleSection('business')}
                    onNext={() => form.goToNextFrom('business')}
                />
            )}

            <ReviewSection visible={form.activeSection === 'review'}>
                <ReferralCard>
                    <ReferralCodeInput
                        referralCode={form.referralCode}
                        setReferralCode={form.setReferralCode}
                        valid={form.referralCodeValid}
                        setValid={form.setReferralCodeValid}
                    />
                </ReferralCard>

                <ConsentCheckbox consent={form.consent} setConsent={form.setConsent} />

                <SubmitArea container item>
                    <Grid item xs={12} md={6}>
                        <PrimaryButton
                            id="signup-submit-button"
                            disabled={form.buttonDisabled}
                            loading={form.isSubmitting}
                            onClick={form.handlePostUser}
                        >
                            {actionMessage}
                        </PrimaryButton>
                    </Grid>
                </SubmitArea>

                <ErrorContainer>{form.error && <Alert severity="error">{form.error}</Alert>}</ErrorContainer>
            </ReviewSection>

            <FormFooter />
        </Grid>
    );
}

function FormFooter() {
    return (
        <FooterContainer>
            <Text variant="caption" color={Colors.mediumGrey}>
                * Indicates required field
            </Text>
            <Text variant="caption" color={Colors.mediumGrey} textStyle={{ marginTop: 4 }}>
                ** Required in the state of Ohio and Illinois
            </Text>
            <Text variant="body2" color={Colors.mediumGrey} textStyle={{ marginTop: 12 }}>
                Need help signing up? Email us at{' '}
                <a href="mailto:support@join-tandem.com" style={{ color: Colors.turquoise }}>
                    support@join-tandem.com
                </a>
            </Text>
        </FooterContainer>
    );
}

const ReviewSection = styled('div', {
    shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ visible }) => ({
    display: visible ? 'block' : 'none',
    marginTop: 4,
}));

const ReferralCard = styled('div')({
    backgroundColor: Colors.white,
    borderRadius: 12,
    border: `1px solid ${Colors.lightGrey}`,
    padding: '20px 24px',
    marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
});

const SubmitArea = styled(Grid)({
    marginTop: 8,
    marginBottom: 8,
});

const ErrorContainer = styled('div')({
    minHeight: 48,
    marginTop: 4,
});

const FooterContainer = styled('div')({
    paddingTop: 16,
    paddingBottom: 24,
});
